// Taşeron portalı — İş İzinleri listesi (api-contract §21, `GET /supplier/permits`).
// Yetkili **kendi contractor'ının** izinlerini görür (salt-okunur — İSG uzmanı §14'te yönetir):
// görev türü, başlık (+ tehlike), **sahalar (çoklu — §14 Faz 2; ilk N + "+X")**, geçerlilik,
// **durum rozeti** (türetilmiş Expired dahil),
// personel sayısı ve **hazır rozeti** (readyPersonnelCount/personnelCount → tümü hazır mı).
// Filtreler: durum + arama (başlık); sayfalı. Firma/contractor kimliği GÖNDERİLMEZ — sunucu
// bağdan çözer; bağ yok (404) / erişim yok (403) → nazik durum ekranı (ContractorStateNotice).
// Satırın başlığı detaya bağlantıdır (→ /permits/{uuid}; S37-2: ayrı "Detay" düğmesi yok); İki katmanlı authz UI: bu ekran yalnız `supplier-contractor`
// (Yetkili) Gate'i altındadır — Personeli GÖRMEZ.

import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  HazardTag,
  PageHeader,
  Pagination,
  SelectMenu,
  type SelectOption,
  type SupplierPermitDto,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  type TagColor,
  currentCulture,
  supplierPermitApi,
  useAuthStore,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractorStateNotice } from '../components/ContractorStateNotice';
import { PERMIT_STATUSES } from '../components/permits/permitEnums';
import { permitStatusPresentation, readinessColor } from '../components/status/tone';
import { formatDate } from '../lib/format';
import { supplierPermitKeys } from '../lib/supplierPermitKeys';
import { useTableDensity } from '../lib/uiPrefs';
import { useDebouncedValue } from '../lib/useDebouncedValue';

const PAGE_SIZE = 20;
/** Liste saha kolonunda satır içinde gösterilecek saha adı sayısı (kalanı "+X"). */
const MAX_SITES_INLINE = 2;
const COLUMNS = 6;

/**
 * İzin satırında sahaları özetler (**çoklu lokasyon — §14 Faz 2**): ilk N ad virgüllü + kalan
 * "+X"; saha yoksa tire. Ada göre sıralı gelir (backend). Tek satır, taşan kısım elips ile
 * kesilir, tam liste `title`'da (expert `SitesSummary` ile aynı). Detayda tüm sahalar rozet olur.
 */
function SitesSummary({ sites }: { sites: SupplierPermitDto['sites'] }) {
  const { t } = useTranslation();
  if (sites.length === 0) {
    return <>—</>;
  }
  const shown = sites.slice(0, MAX_SITES_INLINE);
  const extra = sites.length - shown.length;
  return (
    <span className="block truncate" title={sites.map((s) => s.name).join(', ')}>
      {shown.map((s) => s.name).join(', ')}
      {extra > 0 ? (
        <span className="ml-1 text-xs font-medium text-muted tnum">
          {t('permits.list.sitesMore', { count: extra })}
        </span>
      ) : null}
    </span>
  );
}

/** Geçerlilik aralığı İKİ SATIRDA (başlangıç / "– bitiş") — dar sütunda kırpılmaz (expert ile aynı). */
function ValidityRange({
  start,
  end,
  culture,
}: {
  start: string | null | undefined;
  end: string | null | undefined;
  culture: string;
}) {
  if (!start || !end) {
    return <>—</>;
  }
  return (
    <>
      <span className="block truncate">{formatDate(start, culture)}</span>
      <span className="block truncate">{`– ${formatDate(end, culture)}`}</span>
    </>
  );
}

/**
 * Hazır personel etiketi — tümü hazır (readyPersonnelCount === personnelCount, count>0) → yeşil;
 * en az biri eksik → kırmızı (design review M3: "hazır değil" kırmızı); kapsanan personel yok /
 * `readyPersonnelCount` null (bilinmiyor) → gri, sayıyı türetmeden.
 */
function readinessSummary(permit: SupplierPermitDto): {
  color: TagColor;
  ready: number | null;
  total: number;
} {
  const total = permit.personnelCount;
  const ready = permit.readyPersonnelCount;
  if (ready == null || total === 0) {
    return { color: 'gray', ready, total };
  }
  return { color: readinessColor(ready >= total), ready, total };
}

export function PermitsListPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [density, setDensity] = useTableDensity();

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permits.list.filter.allStatuses') },
      ...PERMIT_STATUSES.map((s) => ({ value: s, label: t(`permits.status.${s}`) })),
    ],
    [t],
  );

  const params = {
    status: (status || undefined) as SupplierPermitDto['status'] | undefined,
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: supplierPermitKeys.listParams(companyId, params),
    queryFn: () => supplierPermitApi.listSupplierPermits(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const hasActiveFilters = Boolean(debouncedSearch || status);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function clearFilters() {
    setSearch('');
    setStatus('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('permits.title') }]} />
      {/* Eylem YOK — taşeron izin oluşturamaz (salt-okunur, İSG uzmanı yönetir). */}
      <PageHeader title={t('permits.title')} helperText={t('permits.list.subtitle')} />

      {query.isError ? (
        <ContractorStateNotice error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <Card className="carbon:p-0">
          <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
            <TableToolbar
              summary={
                query.data
                  ? t('permits.list.count', { count: query.data.filteredTotal })
                  : undefined
              }
              search={search}
              onSearchChange={resetPage(setSearch)}
              density={density}
              onDensityChange={setDensity}
              densityLabel={t('common.compactRows')}
              searchLabel={t('permits.list.search')}
              searchPlaceholder={t('permits.list.searchPlaceholder')}
              filters={
                <TableToolbarFilter>
                  <Field htmlFor="permits-filter-status" label={t('permits.list.filter.status')}>
                    <SelectMenu
                      id="permits-filter-status"
                      placeholder={t('permits.list.filter.allStatuses')}
                      options={statusOptions}
                      value={status}
                      onValueChange={resetPage(setStatus)}
                    />
                  </Field>
                </TableToolbarFilter>
              }
            />

            {/* Tablo tabanı 920px (design review M6; 1280'de ana kaydırma çubuğuyla içerik ≈945px → kayma YOK;
                expert 960 kullanır, burada çubuk payı düşüldü); daha dar görünümde kaydırma YALNIZ `Table`ın kendi sarmalayıcısında
                (sayfa yana kaymaz). `table-fixed`: sabit px sütunlar + Başlık (genişliksiz) kalanı alır. */}
            <Table
              caption={t('permits.title')}
              density={density}
              className="table-fixed min-w-[920px]"
            >
              <TableHeader>
                <TableRow>
                  <TableHead>{t('permits.list.col.title')}</TableHead>
                  <TableHead className="w-[184px]">{t('permits.list.col.status')}</TableHead>
                  <TableHead className="w-[160px]">{t('permits.list.col.permitType')}</TableHead>
                  <TableHead className="w-[15%]">{t('permits.list.col.sites')}</TableHead>
                  <TableHead className="w-[136px]">{t('permits.list.col.validity')}</TableHead>
                  <TableHead className="w-[120px]">{t('permits.list.col.readiness')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody aria-busy={query.isPending || undefined}>
                {query.isPending ? (
                  <TableSkeletonRows columns={COLUMNS} />
                ) : rows.length === 0 ? (
                  <TableMessageRow colSpan={COLUMNS}>
                    {hasActiveFilters ? (
                      <EmptyState
                        icon={SearchX}
                        title={t('permits.list.noResults')}
                        description={t('common.noResults.body')}
                        action={
                          <Button variant="ghost" size="sm" onClick={clearFilters}>
                            {t('common.clearFilters')}
                          </Button>
                        }
                        role="status"
                      />
                    ) : (
                      <EmptyState icon={ClipboardCheck} title={t('permits.list.empty')} />
                    )}
                  </TableMessageRow>
                ) : (
                  rows.map((permit) => {
                    const readiness = readinessSummary(permit);
                    const presentation = permitStatusPresentation(permit.status);
                    return (
                      <TableRow
                        key={permit.uuid}
                        className="carbon:h-auto carbon:min-h-[var(--row-h)]"
                      >
                        <TableCell truncate={false} className="carbon:py-2">
                          <TableLink
                            to={`/permits/${permit.uuid}`}
                            title={permit.title}
                            className="line-clamp-2"
                          >
                            {permit.title}
                          </TableLink>
                          <HazardTag hazardClass={permit.hazardClass} className="mt-1" />
                        </TableCell>
                        <TableCell>
                          <Tag color={presentation.color} title={t(presentation.labelKey)}>
                            {t(presentation.labelKey)}
                          </Tag>
                        </TableCell>
                        <TableCell truncate={false} className="text-muted carbon:py-2">
                          <span className="line-clamp-2" title={permit.permitTypeName ?? undefined}>
                            {permit.permitTypeName ?? '—'}
                          </span>
                        </TableCell>
                        <TableCell truncate={false} className="text-muted carbon:py-2">
                          <SitesSummary sites={permit.sites} />
                        </TableCell>
                        <TableCell truncate={false} className="text-muted tnum carbon:py-2">
                          <ValidityRange
                            start={permit.validityStart}
                            end={permit.validityEnd}
                            culture={culture}
                          />
                        </TableCell>
                        <TableCell>
                          {readiness.total === 0 ? (
                            <span className="text-muted">{t('permits.readiness.noPersonnel')}</span>
                          ) : (
                            <Tag color={readiness.color} className="tnum">
                              {readiness.ready == null
                                ? t('permits.readiness.personnelOnly', { total: readiness.total })
                                : t('permits.readiness.summary', {
                                    ready: readiness.ready,
                                    total: readiness.total,
                                  })}
                            </Tag>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {query.data ? (
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                filteredTotal={query.data.filteredTotal}
                onPageChange={setPage}
                labels={{
                  nav: t('permits.pagination.nav'),
                  previous: t('permits.pagination.previous'),
                  next: t('permits.pagination.next'),
                }}
              />
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
